import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 30,
    fontFamily: 'Helvetica',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 30,
    paddingBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: '#1E40AF',
  },
  companyName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1E40AF',
  },
  subtitle: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 4,
  },
  mainTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 20,
    textAlign: 'center',
  },
  infoText: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#111827',
    marginTop: 20,
    marginBottom: 15,
  },
  card: {
    flexDirection: 'row',
    marginBottom: 15,
    padding: 15,
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  cardLeft: {
    flex: 1,
  },
  serviceName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 8,
  },
  serviceText: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  costSummary: {
    marginTop: 30,
    padding: 20,
    backgroundColor: '#F3F4F6',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#D1D5DB',
  },
  costTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 15,
    textAlign: 'center',
  },
  costText: {
    fontSize: 14,
    color: '#374151',
    marginBottom: 8,
    textAlign: 'center',
  },
  totalLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1E40AF',
    marginTop: 10,
    textAlign: 'center',
  },
  disclaimerText: {
    fontSize: 10,
    color: '#6B7280',
    marginTop: 30,
    textAlign: 'center',
    fontStyle: 'italic',
  },
});

export interface ReportingQuotePDFProps {
  tierName: string;
  machineCount: number;
  skuCount: number;
  monthlyCost: number;
  contact?: {
    fullName?: string;
    email?: string;
    company?: string;
  };
  logoSrc?: string;
}

export const ReportingQuotePDF: React.FC<ReportingQuotePDFProps> = ({ 
  tierName, 
  machineCount, 
  skuCount, 
  monthlyCost, 
  contact 
}) => {
  const dateStr = new Date().toLocaleDateString();

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View>
            <Text style={styles.companyName}>Bright.Blue</Text>
            <Text style={styles.subtitle}>Reporting & Analytics Solutions</Text>
          </View>
        </View>

        {/* Title and Info */}
        <Text style={styles.mainTitle}>Reporting & Analytics Quote</Text>
        <Text style={styles.infoText}>Date: {dateStr}</Text>
        {contact?.fullName && <Text style={styles.infoText}>Contact: {contact.fullName}</Text>}
        {contact?.email && <Text style={styles.infoText}>Email: {contact.email}</Text>}
        {contact?.company && <Text style={styles.infoText}>Company: {contact.company}</Text>}

        <Text style={styles.sectionTitle}>Selected Reporting Package</Text>
        <View style={styles.card}>
          <View style={styles.cardLeft}>
            <Text style={styles.serviceName}>{tierName}</Text>
            <Text style={styles.serviceText}>Machine Count: {machineCount} machines</Text>
            <Text style={styles.serviceText}>SKU Count: {skuCount} SKUs</Text>
            <Text style={styles.serviceText}>Monthly Cost: £{monthlyCost.toLocaleString('en-GB')}</Text>
          </View>
        </View>

        {/* Cost Summary */}
        <View style={styles.costSummary}>
          <Text style={styles.costTitle}>Investment Summary</Text>
          <Text style={styles.costText}>Monthly Reporting Cost: £{monthlyCost.toLocaleString('en-GB')}</Text>
          <Text style={styles.totalLabel}>Total Monthly Cost: £{monthlyCost.toLocaleString('en-GB')}</Text>
        </View>

        {/* Disclaimer */}
        <Text style={styles.disclaimerText}>
          This estimate is valid for 7 days from the date of issue and is subject to approval by an authorized Bright.Blue representative. Pricing is not binding until an official Bright.Blue quote has been issued and signed off.
        </Text>
      </Page>
    </Document>
  );
};
